import { Controller } from "@hotwired/stimulus"

// Vertical tabs: one panel visible, arrow keys move between tabs, the
// counter and pagination follow the selection.
export default class extends Controller {
  static targets = ["tablist", "item", "tab", "panel", "counter", "pagination"]
  static values = { selected: Number }

  select(event) {
    this.activate(Number(event.currentTarget.dataset.index))
  }

  // Pagination links are rendered as "#<page>"; a page is a 1-based index.
  paginate(event) {
    const link = event.target.closest("a[href^='#']")
    if (!link) return
    event.preventDefault()
    const page = Number(link.getAttribute("href").slice(1))
    if (Number.isFinite(page) && page >= 1) this.activate(page - 1)
  }

  keydown(event) {
    const index = Number(event.currentTarget.dataset.index)
    const last = this.tabTargets.length - 1
    let next
    switch (event.key) {
      case "ArrowDown": next = index === last ? 0 : index + 1; break
      case "ArrowUp": next = index === 0 ? last : index - 1; break
      case "Home": next = 0; break
      case "End": next = last; break
      default: return
    }
    event.preventDefault()
    this.activate(next)
    this.tabTargets[next].focus()
  }

  activate(index) {
    if (index < 0 || index >= this.tabTargets.length) return
    this.selectedValue = index

    this.tabTargets.forEach((tab, i) => {
      const selected = i === index
      tab.setAttribute("aria-selected", String(selected))
      tab.tabIndex = selected ? 0 : -1
    })
    this.itemTargets.forEach((item, i) => {
      item.classList.toggle("MediaPlaylist__item--selected", i === index)
    })
    this.panelTargets.forEach((panel, i) => {
      panel.hidden = i !== index
    })
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1}/${this.tabTargets.length}`
    }
    this.scrollListTo(index)
    this.syncPagination(index)
  }

  scrollListTo(index) {
    const item = this.itemTargets[index]
    if (!item || typeof this.tablistTarget.scrollTo !== "function") return
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.tablistTarget.scrollTo({
      top: Math.max(0, item.offsetTop - this.tablistTarget.offsetTop),
      behavior: reduced ? "auto" : "smooth"
    })
  }

  // The pagination component is server-rendered for the initial page; from
  // here on only its previous/next hrefs need to point at the neighbours.
  syncPagination(index) {
    if (!this.hasPaginationTarget) return
    const last = this.tabTargets.length - 1
    const links = this.paginationTarget.querySelectorAll("a[href^='#']")
    for (const link of links) {
      const label = (link.getAttribute("aria-label") || "").toLowerCase()
      if (label.startsWith("previous")) {
        link.setAttribute("href", `#${Math.max(1, index)}`)
        link.setAttribute("aria-disabled", String(index === 0))
      } else if (label.startsWith("next")) {
        link.setAttribute("href", `#${Math.min(last, index + 1) + 1}`)
        link.setAttribute("aria-disabled", String(index === last))
      }
    }
  }
}
